/**
 * Tenant-level nav visibility — the LAST filter between /v6/nav and the app.
 *
 * WHY THIS IS ITS OWN FILE. App.tsx applies a hardcoded, client-side hide on
 * g2 ("hide aviation-ops menus Denis doesn't demo") and then generates the
 * module route table from the FILTERED list. So this filter does not merely
 * hide a menu — it decides which modules have a <Route> at all. Anything else
 * that reasons about "can this install open X?" therefore has to apply the
 * exact same rule, or it will disagree with the router.
 *
 * That is not hypothetical: `useSectionReachability` reads /v6/nav directly to
 * decide whether a section-home tile links or renders unavailable. Reading the
 * RAW nav, it saw ten routable Flight Admin children and would have rendered
 * ten working links — every one of which lands on NotFound, because App.tsx
 * had already filtered the whole section out before building the routes. A
 * gate that exists precisely to prevent dead links would have manufactured
 * ten of them. Extracting the rule here is what makes the two agree by
 * construction rather than by two people remembering.
 *
 * Keep this the ONLY definition. If the g2 hide is ever lifted, lifting it
 * here lifts it everywhere at once.
 */
import { ConfigService } from "../config/ConfigService";
import { getInstallName, getProduct, isPureV6Tenant } from "../config/product";

import type { IAeronetNav, IAeronetNavItem } from "./types";

/**
 * Top-level nav sections hidden per product, matched on lowercased name OR id.
 * Ids from /v6/nav are NUMBERS — coerce via String() (a bare .toLowerCase()
 * on item.id crashed setUpNav for every tenant, 2026-08-21).
 *
 * ── g2 IS EMPTY AS OF 2026-08-29, DELIBERATELY. DO NOT RE-ADD WITHOUT A RULING. ──
 * It used to hold ["flight admin", "flight_admin", "pilots", "pilot"], added as
 * "G2 demo only: hide aviation-ops menus Denis doesn't demo". Aaron lifted it,
 * 2026-08-29, verbatim: "g2 is mustang - dont make the distinction same company
 * group - same people in in the meeting."
 *
 * So the filter's PREMISE was wrong, not merely outweighed: it defended a
 * boundary between two audiences who are the same people in the same room.
 * Recording that here rather than deleting the history, because the absence of
 * a filter is indistinguishable from a filter nobody got round to writing —
 * and because this entry cost four parallel sessions hours of DB archaeology
 * while the deciding fact sat in the client. Empty is the answer, not an
 * oversight.
 *
 * The mechanism stays for a future tenant that genuinely needs a hide: add a
 * product key with its own set. `isNavSectionHidden` is exported and consumed
 * by SectionDashboard's reachability gate, so anything added here immediately
 * grades those tiles unavailable rather than shipping dead links.
 */
const HIDDEN_NAV_BY_PRODUCT: Record<string, ReadonlySet<string>> = {
  g2: new Set<string>(),
  demo: new Set<string>(),
};

/** True when this tenant hides `name` from the top nav (and thus from routing). */
export function isNavSectionHidden(name: unknown, id?: unknown): boolean {
  const hidden = HIDDEN_NAV_BY_PRODUCT[getProduct()];
  if (!hidden || hidden.size === 0) return false;
  const n = String(name ?? "").toLowerCase().trim();
  const i = String(id ?? "").toLowerCase().trim();
  return hidden.has(n) || (!!i && hidden.has(i));
}

/** Apply the tenant hide to a raw /v6/nav payload. Identity off g2. */
export function filterNavForTenant<T extends Pick<IAeronetNav, "id" | "name">>(
  navData: readonly T[]
): T[] {
  return navData.filter((item) => !isNavSectionHidden(item.name, item.id));
}

/** The zone the legacy (Roxen) installs live on: `<installName>.aeronet.nz`. */
const LEGACY_INSTALL_ZONE = "aeronet.nz";

/**
 * True when `url` opens THIS tenant's own legacy install, `<installName>.aeronet.nz`.
 * gch and volcanic have one (their `global_site_domain` points at it, and their
 * users work there); g2, mustang, demo and aquipe point at `crm.aeronet.nz`,
 * another customer's install.
 */
export function isOwnLegacyInstallUrl(url: unknown, installName: string): boolean {
  try {
    const host = new URL(String(url ?? "")).hostname.toLowerCase();
    return !!installName && host === `${installName.toLowerCase()}.${LEGACY_INSTALL_ZONE}`;
  } catch {
    return false;
  }
}

/**
 * A pure-v6 tenant never renders a menu item as an off-tenant href. Aaron
 * 2026-10-14: "there should be no legacy modules in the mustang or g2 menus".
 *
 * App.tsx gives a nav item a `to` only when Features.tsx registers its key.
 * Without one, Menu.tsx and MenuDrawer.tsx render `<a href={menuItem.url}>`, and
 * get_nav builds that url from the tenant's `global_site_domain`, which is
 * `https://crm.aeronet.nz/` on g2, mustang, demo and aquipe. Stores › Parts on
 * mustang (nav 263, mod_function `parts`) sent a Mustang user to the CRM install
 * in the Mustang demo.
 *
 * So, on isPureV6Tenant(), an item without a `to` stays only when its url is the
 * tenant's OWN legacy install (gch, volcanic), or when the tenant's
 * `nav_legacy_href_allow` config row lists its key (see loadLegacyHrefAllow).
 * Everything else is dropped, and a section this empties is dropped with it:
 * get_nav never sends a childless section, so neither should this. Off pure-v6
 * tenants (crm, scm, ops, mt, mro) the payload is returned as-is: they keep
 * their legacy install links.
 *
 * Run after App.tsx has set `to` on every item it can route.
 */
export function dropOffTenantLegacyItems(
  navData: readonly IAeronetNav[],
  legacyHrefAllow: ReadonlySet<string> = new Set()
): IAeronetNav[] {
  if (!isPureV6Tenant()) return [...navData];
  const installName = getInstallName();
  return navData.flatMap((section) => {
    const children = section.children ?? [];
    const kept = children.filter(
      (item) =>
        !!item.to ||
        isOwnLegacyInstallUrl(item.url, installName) ||
        legacyHrefAllow.has(navItemKey(item))
    );
    if (kept.length === children.length) return [section];
    return kept.length > 0 ? [{ ...section, children: kept }] : [];
  });
}

/**
 * The key App.tsx resolves a nav item by: `remote_menu_key` for a `url` /
 * `custom` row, otherwise `function` (the row's mod_function). Lowercased.
 */
export function navItemKey(item: Pick<IAeronetNavItem, "function" | "remote_menu_key">): string {
  const fn = String(item.function ?? "");
  const key = fn === "url" || fn === "custom" ? item.remote_menu_key : fn;
  return String(key ?? "").toLowerCase().trim();
}

/**
 * The per-tenant exception to dropOffTenantLegacyItems: config row
 * config_function `nav`, config_variable `nav_legacy_href_allow`, a comma list
 * of nav keys (mod_functions) allowed to render as a legacy href although they
 * have no v6 route. No row means an empty list, and that is the default on
 * every tenant. g2 lists `tickets` (Aaron 2026-10-14: "Keep Tickets visible").
 * The value lives in the tenant's own database, never as a tenant name here.
 */
export const LEGACY_HREF_ALLOW_CONFIG = { app: "nav", variable: "legacy_href_allow" } as const;

/** Parse a `nav_legacy_href_allow` value: comma separated, case-insensitive, blanks dropped. */
export function parseLegacyHrefAllow(value: unknown): Set<string> {
  return new Set(
    String(value ?? "")
      .split(",")
      .map((key) => key.toLowerCase().trim())
      .filter(Boolean)
  );
}

/**
 * Read this tenant's `nav_legacy_href_allow`. It never throws, and it does not
 * call the API off pure-v6 tenants, where the guard does not run. If the read
 * fails, the result is the empty list: the allowlisted rows are hidden until the
 * next load, rather than any off-tenant link being shown.
 */
export async function loadLegacyHrefAllow(): Promise<Set<string>> {
  if (!isPureV6Tenant()) return new Set();
  const { app, variable } = LEGACY_HREF_ALLOW_CONFIG;
  try {
    const config = await ConfigService.query(app, variable);
    return parseLegacyHrefAllow(config?.[`${app}_${variable}`]);
  } catch (e) {
    console.error("nav_legacy_href_allow config read failed", e);
    return new Set();
  }
}

/**
 * Update Maintenance menu children to match current product requirements:
 * Remove: ATA Chapters, Role Equip, Operator Hours, Flights, Log Book / Tech Log
 * Add:    MRO Work Orders — and ONLY that one.
 *
 * This header used to read "Add: MRO Work Orders, Service Orders, Freight". Both
 * of the other two were injections of a module the tenant was never granted, and
 * both have since been removed (Service Orders 2026-09-03, Freight 2026-09-04);
 * the line outlived them by a day each. A stale ADD list here is worse than no
 * list, because it is the first thing read when deciding whether an unexpected
 * menu row is intentional. Keep it in step with the `toAdd` pushes below.
 */
export function sanitizeMaintenanceNav(navData: readonly IAeronetNav[]): IAeronetNav[] {
  const updated = navData.map((item) => {
    const nameLower = String(item.name || "").toLowerCase().trim();
    if (nameLower === "maintenance") {
      const filteredChildren = (item.children || []).filter((child) => {
        const cName = String(child.name || "").toLowerCase().trim();
        const cFunc = String(child.function || child.remote_menu_key || child.id || "").toLowerCase().trim();

        if (
          cName === "ata" || cName === "ata chapters" || cFunc.includes("ata") ||
          cName === "role equip" || cName === "role equipment" || cFunc.includes("role_equip") || cFunc.includes("role-equipment") ||
          cName === "operator hours" || cName === "flight & duty" || cFunc.includes("flightduty") ||
          cName === "flights" || cFunc === "flights" ||
          cName === "log book" || cName === "logbook" || cName === "flight logbook" || cName === "tech log" || cName === "maintenance log" || cFunc === "log-book" || cFunc === "tech-log" || cFunc === "maintenance-log"
        ) {
          return false;
        }
        return true;
      });

      const existingNames = new Set(filteredChildren.map((c) => String(c.name || c.function || c.to || "").toLowerCase()));

      const toAdd: any[] = [];
      if (!existingNames.has("mro work orders") && !existingNames.has("work orders") && !existingNames.has("work-orders")) {
        toAdd.push({
          id: "work-orders",
          name: "MRO Work Orders",
          function: "work-orders",
          to: "work-orders",
          target: "",
          is_active: true,
          children: [],
        });
      }
      // NO "Service Orders" IS INJECTED. Removed 2026-09-03.
      //
      // ⚠️ BLAST RADIUS IS EVERY TENANT, NOT g2. sanitizeMaintenanceNav is
      // applied to every /v6/nav payload (App.tsx), so this changes the LIVE
      // Maintenance menu on all nine services, not just the g2 demo.
      //
      // It was injected with `function: "service-orders"`, which binds to
      // NOTHING: the Features.tsx key for the module is `tds` (:461 ->
      // ServiceOrders, src/service-orders/), and the module's only static route
      // is `serviceorders/*` (App.tsx, no hyphen). So the hyphenated key matched
      // neither a feature nor a static path, App.tsx emitted no <Route>, and the
      // item rendered and then 404'd through the `path="*"` catch-all — on every
      // tenant. Found by src/__tests__/defaultG2NavKeys.test.ts, not by review.
      //
      // REBOUND TO `tds` WOULD HAVE BEEN WRONG, which is the part worth keeping.
      // Measured on module_g2_demo 2026-09-03: a real, GRANTED "Service Orders"
      // row already renders — id 10144, mod_function `tds`, parent 505
      // Purchasing, 3 group grants. Pointing this injection at `tds` would
      // therefore put a SECOND "Service Orders" in the menu, both routing to
      // /app/tds — the same duplicate-path defect just removed from PILOTS
      // (/weather) and avoided in DEFAULT_G2_NAV (/personnel). The real granted
      // row stays the single source.
      //
      // NO "Freight" IS INJECTED. Removed 2026-09-04 (punch-list 4.6).
      //
      // Same class as the Service Orders removal above, and removed the same
      // way: a client-side row for a module the tenant was never GRANTED. It
      // differs from that one only in that it bound correctly and routed —
      // which made it a silent DUPLICATE rather than a 404, and so it survived
      // the 2026-09-03 pass.
      //
      // POPULATION, measured 2026-09-04 with `repo/scripts/navrender.py` against
      // every tenant DB rather than reasoned about. The injection fires only on
      // a tenant whose TOP-LEVEL section list contains one named "Maintenance"
      // that has no Freight child — exactly three of the nine:
      //
      //   g2   Maintenance(504): Timesheets, Parts Orders, Tasks, Work Orders,
      //        Production            -> fired. Freight ALREADY renders, granted,
      //                                 under Purchasing(505) = punch-list 5.1.
      //                                 Removal de-duplicates. No access lost.
      //   mro  Maintenance(504): Tasks, Work Orders
      //                              -> fired. Freight ALREADY renders, granted,
      //                                 under BOTH Purchasing(505) and
      //                                 Stores(506). Removal de-duplicates.
      //   mt   Maintenance(504): Tasks, Work Orders
      //                              -> fired. mt has NO Freight anywhere else:
      //                                 no Purchasing section, no Stores section.
      //                                 ⚠ Removal takes Freight OFF the mt menu.
      //
      //   scm · crm · ops · aquipe · gch · volcanic — none has a TOP-LEVEL
      //   section named "Maintenance", so the injection never fired there and
      //   this change is a no-op for them. (gch has a CHILD named "Maintenance"
      //   under its CRM section; the match is on section name, so it never hit.)
      //
      // The mt consequence is deliberate and is NOT a regression to fix here.
      // mt reaching Freight at all was an artefact of this injection, never a
      // grant — the module rows and group grants say mt was not given Freight.
      // If mt SHOULD have it, that is a `modules`/grant row on the shared DB
      // (a mutation card), not a fabricated client-side menu entry. Raised to
      // the master as the policy half of this row.
      //
      // `work-orders` above is NOT equivalent and stays: it is suppressed on g2
      // by its own guard, because Maintenance there carries a real granted
      // "Work Orders" (145, `workorders`).

      return {
        ...item,
        children: [...filteredChildren, ...toAdd],
      };
    }

    if (nameLower === "stores") {
      const filteredChildren = (item.children || []).filter((child) => {
        const cName = String(child.name || "").toLowerCase().trim();
        const cFunc = String(child.function || child.remote_menu_key || child.id || "").toLowerCase().trim();

        // Dispatch / Tool Usage is unified into Tool Requests (/app/toolrequests)
        if (cName === "tool usage" || cFunc === "dispatch" || cFunc === "tool_usage") {
          return false;
        }
        return true;
      });
      return {
        ...item,
        children: filteredChildren,
      };
    }
    // NO "Weather" IS INJECTED HERE. Removed 2026-09-03 (code-review B15).
    //
    // A Pilots -> Weather child used to be appended here. It was appended for
    // EVERY tenant, because App.tsx applies sanitizeMaintenanceNav to every
    // nav payload (App.tsx:557) -- unlike DEFAULT_G2_NAV, which was gated on
    // getProduct() === "g2" precisely because aquipe once got G2's aviation
    // menu on a transient nav error (DEFAULT_G2_NAV removed 2026-10-14).
    //
    // Measured 2026-09-03, the reach was wider than "g2" and did not match the
    // tenants named in review. `repo/scripts/navrender.py` per tenant DB:
    //   g2        Pilots renders (6 items)  -> injected
    //   gch       Pilots renders (3 items)  -> injected
    //   volcanic  Pilots renders (3 items)  -> injected
    //   aquipe    Pilots does NOT render    -> never injected
    // gch and volcanic are absent from KNOWN_PRODUCTS (product_context.py:22),
    // so their Host falls through to DEFAULT_PRODUCT "scm" and their Pilots
    // parent (tagged 'scm,crm,ops') passes the filter. The PRODUCT=ops env var
    // on aeronet-fast-api-gch is only the no-request fallback -- do not read it
    // as the tenant's product.
    //
    // Why removal rather than a g2 gate: NO tenant has a Weather `modules` row
    // at all, so this entry was never data-backed anywhere, and a code-injected
    // menu item cannot be product-tagged, granted, ungranted, or seen by
    // navrender.py. The Weather module itself is real and routed
    // (App.tsx:836/844, Features.tsx:143). To put it back on a tenant's menu,
    // seed the nav row -- the INSERT is in the nav-scoping handback.
    return item;
  });

  // NO "Mobile" SECTION IS INJECTED. Removed 2026-09-26 (AG-sweep §4 M4).
  //
  // `ensureMustangMobileNav` used to append a 29-item "Mobile" section on every
  // mustang.aeronet.app nav payload that had no top-level section named
  // "Mobile". Same class as Weather / Service Orders / Freight above: a menu
  // no admin could grant, ungrant, product-tag or see in navrender.py, shown to
  // EVERY mustang user regardless of group. Its only guard was the hostname
  // label (getProduct() === "mustang"), so no other tenant ever got it.
  //
  // The menu now comes from `modules` rows like every other section: a
  // top-level type='nav' "Mobile" parent and children with
  // mod_function = "mobile-emulator", the emulator view carried in the row's
  // own url_query ("&view=parts"). App.tsx maps those rows through
  // `mobileEmulatorTarget` below. No rows = no Mobile menu: /app/mobile-emulator
  // and /mobile/ stay reachable by URL (static routes), nothing 404s.
  // The seed is a mutation card in repo/.coordination/ready/
  // mustang-m4-mobile-nav-2026-09-26.md.
  return updated;
}

/** The nav row function the Mobile emulator is keyed on (Features.tsx + App.tsx). */
export const MOBILE_EMULATOR_FUNCTION = "mobile-emulator";

/**
 * Router target for a `mobile-emulator` nav row. get_nav appends the row's
 * `url_query` to its legacy `url` (…?function=mobile-emulator&nav=N&view=parts),
 * so the emulator view is read back from there — the row, not this file,
 * decides which screen an item opens. A missing or malformed view lands on the
 * emulator's own home rather than guessing.
 */
export function mobileEmulatorTarget(url: unknown): string {
  const view = mobileViewFromUrl(url);
  return view ? `${MOBILE_EMULATOR_FUNCTION}?view=${view}` : MOBILE_EMULATOR_FUNCTION;
}

/**
 * The `view` a `mobile-emulator` nav row names, or null when it names none or
 * a malformed one (strict `[a-z0-9-]+`). ONE parser for the desktop menu's
 * route (above), the emulator's Quick View Jump and — ported verbatim to ES5 —
 * the phone home tiles in public/mobile/js/mobile-nav.js, so a row granted to
 * a user opens the same screen on all three.
 */
export function mobileViewFromUrl(url: unknown): string | null {
  const raw = String(url ?? "");
  const q = raw.indexOf("?");
  const view = q < 0 ? null : new URLSearchParams(raw.slice(q + 1)).get("view");
  return view && /^[a-z0-9-]+$/.test(view) ? view : null;
}
